import React from 'react';

import {
  Alert,
  Button,
  Grid,
  DescriptionList,
  DescriptionItem,
  Typography,
  Stack,
  Section,
  SidePage,
  makeStyles,
  IconButton,
  PostAddIcon,
  AddIcon,
  ContentCopyIcon,
} from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';

import AffiliateNote from 'scenes/Campaigns/components/AffiliateNote';
import { CampaignDetails } from 'scenes/Campaigns/GGCampaigns/components/CampaignDetails';
import CampaignsLinksTable from 'scenes/Campaigns/GGCampaigns/components/CampaignsLinksTable';
import { useCopyLink } from 'scenes/Campaigns/hooks';

import {
  ACTION_ADD_ANOTHER,
  ACTION_ADD_PROMO_CODE,
  ACTION_DUPLICATE,
  ACTION_EXPORT_LINKS,
} from './constants';

const useStyles = makeStyles(() => ({
  alert: {
    '& .MuiAlert-message': {
      padding: 0,
      width: '100%',
    },
  },
}));

export const CampaignCreatedSidePage = ({
  onClose,
  onExited,
  onSubmit,
  open,
  campaigns,
  ...props
}) => {
  const classes = useStyles();
  const [childComponent, openChildComponent] = useChildComponent();
  const copyToClipboard = useCopyLink();
  const handleExportLinks = () => {
    onSubmit(ACTION_EXPORT_LINKS);
  };

  const handleAddPromoCode = () => {
    onSubmit(ACTION_ADD_PROMO_CODE);
  };

  const handleAddAnother = () => {
    onSubmit(ACTION_ADD_ANOTHER);
  };

  const handleDuplicate = () => {
    onSubmit(ACTION_DUPLICATE);
  };

  const handleOpenCampaign = (id) => {
    openChildComponent(CampaignDetails, { id });
  };

  const handleCopyName = (text) => {
    copyToClipboard(text, 'Campaign name copied to your clipboard');
  };

  return (
    <SidePage
      open={open}
      onClose={onClose}
      onExited={onExited}
      backdrop={false}
      {...props}
    >
      <SidePage.Title title={'New Campaigns Details'} />
      <SidePage.NewBody wrapper={true} dense={true}>
        <SidePage.NewContent withSidebar={false}>
          <Section>
            <Alert severity={'success'} className={classes.alert}>
              <Grid
                container
                justifyContent={'space-between'}
                alignItems={'center'}
              >
                <Grid item>
                  <Typography>Campaigns created</Typography>
                </Grid>
                <Grid>
                  <Button type={'button'} onClick={handleExportLinks}>
                    Export Link(s)
                  </Button>
                </Grid>
              </Grid>
            </Alert>
          </Section>

          {campaigns.map(({ campaignLinks = [], name, id }, index) => {
            return (
              <React.Fragment>
                <Section
                  title={name}
                  action={
                    <React.Fragment>
                      <IconButton
                        tooltip={'Copy name'}
                        onClick={() => handleCopyName(name)}
                        icon={<ContentCopyIcon />}
                      />
                      {!index ? (
                        <Button
                          type={'button'}
                          onClick={handleAddPromoCode}
                          icon={<AddIcon />}
                        >
                          Promo Code
                        </Button>
                      ) : (
                        <IconButton
                          tooltip={'Add Promocode'}
                          onClick={handleAddPromoCode}
                        >
                          <PostAddIcon />
                        </IconButton>
                      )}
                    </React.Fragment>
                  }
                >
                  <DescriptionList
                    data={{ name, id }}
                    stripEmpty={true}
                    disableGutters={true}
                  >
                    <DescriptionItem label={'ID'} field={'id'} />
                    <DescriptionItem
                      label={'Name'}
                      field={'name'}
                      type={'link'}
                      onValueClick={() => handleOpenCampaign(id)}
                    />
                  </DescriptionList>
                </Section>
                <Section>
                  <CampaignsLinksTable campaignLinks={campaignLinks} />
                </Section>
              </React.Fragment>
            );
          })}
          <Section>
            <AffiliateNote />
          </Section>
        </SidePage.NewContent>
        {childComponent}
      </SidePage.NewBody>
      <SidePage.Actions>
        <Grid container justifyContent={'space-between'}>
          <SidePage.CloseButton />
          <Stack direction={'row'}>
            <Button type={'button'} onClick={handleDuplicate}>
              Duplicate
            </Button>
            <Button type={'button'} onClick={handleAddAnother}>
              Add Another
            </Button>
          </Stack>
        </Grid>
      </SidePage.Actions>
    </SidePage>
  );
};
